import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mail,
  MessageSquare,
  Bell,
  UserPlus,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Plus,
  Trash2,
  AlertTriangle
} from "lucide-react";

const NotificationSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    welcome_email_enabled: false,
    welcome_sms_enabled: false,
    admin_email_enabled: false,
    admin_sms_enabled: false,
    follow_up_email_enabled: false,
    follow_up_sms_enabled: false,
    admin_emails: [],
    admin_phones: []
  });
  const [status, setStatus] = useState({
    email_configured: false,
    sms_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/notifications`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch notification settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/notifications/status`, { withCredentials: true });
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch notification status:", error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchStatus();
  }, [fetchSettings, fetchStatus]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, settings, { withCredentials: true });
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const addEmail = () => {
    if (newEmail && !settings.admin_emails.includes(newEmail)) {
      setSettings({ ...settings, admin_emails: [...settings.admin_emails, newEmail] });
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setSettings({ ...settings, admin_emails: settings.admin_emails.filter(e => e !== email) });
  };

  const addPhone = () => {
    if (newPhone && !settings.admin_phones.includes(newPhone)) {
      setSettings({ ...settings, admin_phones: [...settings.admin_phones, newPhone] });
      setNewPhone("");
    }
  };

  const removePhone = (phone) => {
    setSettings({ ...settings, admin_phones: settings.admin_phones.filter(p => p !== phone) });
  };

  const sendTestNotification = async (type) => {
    const recipient = type === "email" ? testEmail : testPhone;
    if (!recipient) {
      toast.error(`Please enter a ${type === "email" ? "email address" : "phone number"}`);
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/settings/notifications/test`, { type, recipient }, { withCredentials: true });
      toast.success(`Test ${type} sent successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send test ${type}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cfc-primary font-heading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div data-testid="notification-settings">
      <h1 className="font-heading text-3xl font-bold text-cfc-primary mb-2">Notification Settings</h1>
      <p className="text-cfc-text-muted mb-8">Configure automated email and SMS notifications for your CRM</p>

      {/* Service Status */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Card className={`border-2 ${status.email_configured ? "border-green-500" : "border-yellow-500"}`}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <Mail className={status.email_configured ? "text-green-600" : "text-yellow-600"} />
              <div>
                <p className="font-medium text-cfc-primary">Email Service (SendGrid)</p>
                <p className="text-sm text-cfc-text-muted">
                  {status.email_configured ? "Connected and ready" : "API key not configured"}
                </p>
              </div>
            </div>
            {status.email_configured ? (
              <CheckCircle2 className="text-green-600" />
            ) : (
              <XCircle className="text-yellow-600" />
            )}
          </CardContent>
        </Card>

        <Card className={`border-2 ${status.sms_configured ? "border-green-500" : "border-yellow-500"}`}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <MessageSquare className={status.sms_configured ? "text-green-600" : "text-yellow-600"} />
              <div>
                <p className="font-medium text-cfc-primary">SMS Service (Twilio)</p>
                <p className="text-sm text-cfc-text-muted">
                  {status.sms_configured ? "Connected and ready" : "API keys not configured"}
                </p>
              </div>
            </div>
            {status.sms_configured ? (
              <CheckCircle2 className="text-green-600" />
            ) : (
              <XCircle className="text-yellow-600" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Configuration Notice */}
      {(!status.email_configured || !status.sms_configured) && (
        <Card className="mb-8 border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Configuration Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  To enable notifications, add the following environment variables to your backend .env file:
                </p>
                <div className="mt-3 p-3 bg-white rounded border border-yellow-200 font-mono text-xs">
                  {!status.email_configured && (
                    <>
                      <p className="text-gray-600"># SendGrid Email</p>
                      <p>SENDGRID_API_KEY=your_sendgrid_api_key</p>
                      <p>SENDER_EMAIL=noreply@yourdomain.com</p>
                    </>
                  )}
                  {!status.sms_configured && (
                    <>
                      <p className="text-gray-600 mt-2"># Twilio SMS</p>
                      <p>TWILIO_ACCOUNT_SID=your_account_sid</p>
                      <p>TWILIO_AUTH_TOKEN=your_auth_token</p>
                      <p>TWILIO_PHONE_NUMBER=+1234567890</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Triggers */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-cfc-primary flex items-center gap-2">
            <Bell size={20} />
            Notification Triggers
          </CardTitle>
          <CardDescription>
            Configure when notifications should be sent
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Welcome Notifications */}
          <div className="border-b border-cfc-border pb-6">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="text-cfc-primary" size={18} />
              <h3 className="font-heading font-bold text-cfc-primary">New Lead Welcome</h3>
            </div>
            <p className="text-sm text-cfc-text-muted mb-4">
              Send a welcome message when someone submits the interest form
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-cfc-primary" />
                  <span className="font-medium">Welcome Email to Lead</span>
                </div>
                <Switch
                  checked={settings.welcome_email_enabled}
                  onCheckedChange={() => handleToggle("welcome_email_enabled")}
                  disabled={!status.email_configured}
                  data-testid="toggle-welcome-email"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <MessageSquare size={18} className="text-cfc-primary" />
                  <span className="font-medium">Welcome SMS to Lead</span>
                </div>
                <Switch
                  checked={settings.welcome_sms_enabled}
                  onCheckedChange={() => handleToggle("welcome_sms_enabled")}
                  disabled={!status.sms_configured}
                  data-testid="toggle-welcome-sms"
                />
              </div>
            </div>
          </div>

          {/* Admin Notifications */}
          <div className="border-b border-cfc-border pb-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="text-cfc-primary" size={18} />
              <h3 className="font-heading font-bold text-cfc-primary">Admin Alerts</h3>
            </div>
            <p className="text-sm text-cfc-text-muted mb-4">
              Notify admins when a new lead is submitted
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-cfc-primary" />
                  <span className="font-medium">Email Alert to Admins</span>
                </div>
                <Switch
                  checked={settings.admin_email_enabled}
                  onCheckedChange={() => handleToggle("admin_email_enabled")}
                  disabled={!status.email_configured}
                  data-testid="toggle-admin-email"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <MessageSquare size={18} className="text-cfc-primary" />
                  <span className="font-medium">SMS Alert to Admins</span>
                </div>
                <Switch
                  checked={settings.admin_sms_enabled}
                  onCheckedChange={() => handleToggle("admin_sms_enabled")}
                  disabled={!status.sms_configured}
                  data-testid="toggle-admin-sms"
                />
              </div>
            </div>
          </div>

          {/* Follow-up Reminders */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="text-cfc-primary" size={18} />
              <h3 className="font-heading font-bold text-cfc-primary">Follow-up Reminders</h3>
            </div>
            <p className="text-sm text-cfc-text-muted mb-4">
              Send reminders when follow-ups are due
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-cfc-primary" />
                  <span className="font-medium">Email Reminder</span>
                </div>
                <Switch
                  checked={settings.follow_up_email_enabled}
                  onCheckedChange={() => handleToggle("follow_up_email_enabled")}
                  disabled={!status.email_configured}
                  data-testid="toggle-followup-email"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-cfc-cream rounded">
                <div className="flex items-center gap-3">
                  <MessageSquare size={18} className="text-cfc-primary" />
                  <span className="font-medium">SMS Reminder</span>
                </div>
                <Switch
                  checked={settings.follow_up_sms_enabled}
                  onCheckedChange={() => handleToggle("follow_up_sms_enabled")}
                  disabled={!status.sms_configured}
                  data-testid="toggle-followup-sms"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Contact List */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-cfc-primary">Admin Contact List</CardTitle>
          <CardDescription>
            Who should receive admin notifications?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Admin Emails */}
          <div>
            <Label className="text-cfc-primary font-medium">Admin Email Addresses</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                className="flex-1"
                data-testid="admin-email-input"
              />
              <Button onClick={addEmail} variant="outline" data-testid="add-email-btn">
                <Plus size={18} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {settings.admin_emails.map((email) => (
                <Badge key={email} variant="secondary" className="flex items-center gap-1 py-1 px-3">
                  {email}
                  <button onClick={() => removeEmail(email)} className="ml-1 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </Badge>
              ))}
              {settings.admin_emails.length === 0 && (
                <span className="text-sm text-cfc-text-muted">No admin emails added</span>
              )}
            </div>
          </div>

          {/* Admin Phones */}
          <div>
            <Label className="text-cfc-primary font-medium">Admin Phone Numbers</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="flex-1"
                data-testid="admin-phone-input"
              />
              <Button onClick={addPhone} variant="outline" data-testid="add-phone-btn">
                <Plus size={18} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {settings.admin_phones.map((phone) => (
                <Badge key={phone} variant="secondary" className="flex items-center gap-1 py-1 px-3">
                  {phone}
                  <button onClick={() => removePhone(phone)} className="ml-1 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </Badge>
              ))}
              {settings.admin_phones.length === 0 && (
                <span className="text-sm text-cfc-text-muted">No admin phones added</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Notifications */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-cfc-primary flex items-center gap-2">
            <Send size={20} />
            Test Notifications
          </CardTitle>
          <CardDescription>
            Send test notifications to verify your configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Test Email */}
            <div>
              <Label className="text-cfc-primary font-medium">Test Email</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1"
                  disabled={!status.email_configured}
                  data-testid="test-email-input"
                />
                <Button 
                  onClick={() => sendTestNotification("email")}
                  disabled={testing || !status.email_configured}
                  className="bg-cfc-primary hover:bg-cfc-primary-hover"
                  data-testid="send-test-email-btn"
                >
                  {testing ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>

            {/* Test SMS */}
            <div>
              <Label className="text-cfc-primary font-medium">Test SMS</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+15551234567"
                  className="flex-1"
                  disabled={!status.sms_configured}
                  data-testid="test-phone-input"
                />
                <Button 
                  onClick={() => sendTestNotification("sms")}
                  disabled={testing || !status.sms_configured}
                  className="bg-cfc-primary hover:bg-cfc-primary-hover"
                  data-testid="send-test-sms-btn"
                >
                  {testing ? "Sending..." : "Send"}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveSettings}
          disabled={saving}
          className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary font-bold px-8"
          data-testid="save-settings-btn"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;
